import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Input,
  Label,
  Field,
  Dropdown,
  Option,
  Textarea,
  RadioGroup,
  Radio,
  Spinner,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Text,
  Switch,
  TabList,
  Tab,
  MessageBar,
  MessageBarBody,
} from "@fluentui/react-components";
import {
  Dismiss24Regular,
  Save24Regular,
  Add16Regular,
  Edit16Regular,
  Delete16Regular,
  Copy16Regular,
  Pause16Regular,
  Play16Regular,
  Star16Filled,
  Star16Regular,
  Sparkle16Regular,
  Bed16Regular,
  WeatherMoon16Regular,
  People16Regular,
  Calendar16Regular,
  Search16Regular,
  Checkmark12Regular,
} from "@fluentui/react-icons";

import { HzApiError, hzFetch, hzGet, hzPropertyId } from "./Api/hzApi";

import styles from "./Offers.module.css";

type Offer = {
  id: string;
  title: string;
  description: string;
  roomCategory: string;
  roomTypeId?: number;
  price: number;
  originalPrice?: number;
  validFrom: string;
  validTo: string;
  type: string;
  priceBasis: "per night" | "per package";
  minNights: number;
  maxGuests: number;
  code: string;
  inclusions: string[];
  channels: string[];
  cancellation: string;
  limit: number;
  used: number;
  featured: boolean;
  paused: boolean;
};

type Idea = { title: string; why: string; offer: Partial<Offer> };
type RoomTypeOption = { id: number; name: string };

/* ---------- helpers ---------- */
const pad2 = (n: number) => String(n).padStart(2, "0");
const isoD = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const off = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return isoD(d);
};
const fdate = (s: string) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "");
const USD_RATE = 1500; // ₦ per $1 (display only, prices are stored in naira)

const OFFER_TYPES = ["Seasonal", "Package", "Honeymoon", "Early bird", "Last minute", "Long stay", "Weekend", "Corporate", "Family"];
const CANCELLATION = ["Free until 24 hours", "Free until 7 days", "Non-refundable"];
const INCLUSION_PRESETS = ["Breakfast", "Dinner", "Airport pickup", "Spa credit", "Late check-out", "Welcome drink", "Free Wi-Fi", "Free parking", "Room upgrade"];
const CHANNELS = ["Website", "Front desk", "Corporate", "Travel agents", "Booking.com", "Expedia"];

const statusOf = (o: Offer): string => (o.paused ? "Paused" : o.used >= o.limit ? "Sold out" : o.validTo < off(0) ? "Expired" : o.validFrom > off(0) ? "Scheduled" : "Active");
const discountOf = (o: Offer) => (o.originalPrice && o.originalPrice > o.price ? Math.round((1 - o.price / o.originalPrice) * 100) : 0);
const blank = (): Offer => ({
  id: "",
  title: "",
  description: "",
  roomCategory: "",
  price: 0,
  validFrom: off(0),
  validTo: off(30),
  type: "Seasonal",
  priceBasis: "per night",
  minNights: 1,
  maxGuests: 2,
  code: "",
  inclusions: [],
  channels: ["Website", "Front desk"],
  cancellation: "Free until 24 hours",
  limit: 100,
  used: 0,
  featured: false,
  paused: false,
});

/* ---------- the database's shape -> the screen's shape ---------- */
type ApiOffer = Omit<Offer, "id" | "originalPrice" | "validFrom" | "validTo"> & { id: number; originalPrice: number | null; validFrom: string; validTo: string };
const fromApi = (o: ApiOffer): Offer => ({
  ...o,
  id: String(o.id),
  originalPrice: o.originalPrice ?? undefined,
  validFrom: String(o.validFrom).slice(0, 10),
  validTo: String(o.validTo).slice(0, 10),
  description: o.description ?? "",
  code: o.code ?? "",
  inclusions: o.inclusions ?? [],
  channels: o.channels ?? [],
});

const RoomOffers: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [currency, setCurrency] = useState<"NGN" | "USD">("NGN");
  const currencySymbol = currency === "NGN" ? "₦" : "$";
  const [offers, setOffers] = useState<Offer[]>([]);
  const [types, setTypes] = useState<RoomTypeOption[]>([]);
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("All");
  const [find, setFind] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState<Offer | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Form state for new/edit offer
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Offer>(blank());

  // room categories are the property's real room types
  const roomCategories = types.map((t) => t.name);
  const typeIdOf = (name: string) => types.find((t) => t.name === name)?.id;
  const AI_IDEAS = ideas;

  const reload = useCallback(async () => {
    const propertyId = hzPropertyId();
    const q = propertyId ? `?propertyId=${propertyId}` : "";
    const [list, opts, idea] = await Promise.all([
      hzGet<{ items: ApiOffer[] }>(`/api/dashboard/offers${q}`),
      hzGet<{ roomTypes: Array<{ id: number; name: string }> }>(`/api/dashboard/rooms/form-options${q}`),
      hzGet<Array<{ title: string; why: string; offer: Record<string, unknown> & { roomTypeId?: number } }>>(`/api/dashboard/offers/ideas${q}`).catch(() => []),
    ]);
    const roomTypes = opts.roomTypes.map((t) => ({ id: t.id, name: t.name }));
    setTypes(roomTypes);
    setOffers(list.items.map(fromApi));
    setIdeas(
      idea.map((i) => ({
        title: i.title,
        why: i.why,
        offer: { ...(i.offer as Partial<Offer>), roomCategory: roomTypes.find((t) => t.id === i.offer.roomTypeId)?.name ?? "" },
      })),
    );
  }, []);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        await reload();
      } catch (e) {
        if (live) setLoadError(e instanceof Error ? e.message : "The offers could not be loaded.");
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [reload]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(t);
  }, [toast]);

  /** Checks made before sending. The server repeats every one of them. */
  const validate = (f: Offer): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!f.title.trim()) e.title = "Give the offer a title.";
    if (!f.roomCategory || typeIdOf(f.roomCategory) === undefined) e.roomCategory = "Choose a room category.";
    if (!(f.price > 0)) e.price = "Price must be greater than zero.";
    if (f.originalPrice !== undefined && f.originalPrice <= f.price) e.originalPrice = "Original price must be higher than the offer price.";
    if (!f.validFrom || !f.validTo) e.validTo = "Set the start and end dates.";
    else if (f.validTo < f.validFrom) e.validTo = "The end date can't be before the start date.";
    if (!(f.minNights >= 1)) e.minNights = "At least 1 night.";
    if (!(f.maxGuests >= 1)) e.maxGuests = "At least 1 guest.";
    if (f.code && !/^[A-Z0-9]{4,14}$/.test(f.code)) e.code = "Use 4 to 14 capital letters or numbers.";
    else if (f.code && offers.some((o) => o.id !== editingId && o.code === f.code)) e.code = "That code is already used by another offer.";
    if (!(f.limit >= 1)) e.limit = "Set a limit of at least 1.";
    else if (f.limit < f.used) e.limit = `${f.used} have already been used.`;
    if (f.channels.length === 0) e.channels = "Pick at least one channel.";
    return e;
  };

  /** Runs one call against the server, then re-reads the list. Returns false (and shows why) when the server refuses. */
  const run = async (call: () => Promise<unknown>, okMessage: string): Promise<boolean> => {
    setSaving(true);
    try {
      await call();
      await reload();
      setToast(okMessage);
      return true;
    } catch (ex) {
      if (ex instanceof HzApiError && ex.fields && Object.keys(ex.fields).length) setErrors(ex.fields);
      setToast(ex instanceof Error ? ex.message : "That could not be saved.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const body = (f: Offer) => ({
    propertyId: hzPropertyId(),
    title: f.title.trim(),
    description: f.description.trim() || null,
    roomTypeId: typeIdOf(f.roomCategory),
    type: f.type,
    price: f.price,
    originalPrice: f.originalPrice ?? null,
    priceBasis: f.priceBasis,
    validFrom: f.validFrom,
    validTo: f.validTo,
    minNights: f.minNights,
    maxGuests: f.maxGuests,
    code: f.code || null,
    cancellation: f.cancellation,
    limit: f.limit,
    featured: f.featured,
    inclusions: f.inclusions,
    channels: f.channels,
  });

  const handleSaveOffer = async () => {
    const e = validate(formData);
    setErrors(e);
    if (Object.keys(e).length) return;
    const ok = editingId
      ? await run(() => hzFetch(`/api/dashboard/offers/${editingId}`, { method: "PUT", body: JSON.stringify(body(formData)) }), "Offer saved")
      : await run(() => hzFetch(`/api/dashboard/offers`, { method: "POST", body: JSON.stringify(body(formData)) }), "Offer created");
    if (!ok) return;
    setIsAdding(false);
    setEditingId(null);
    setFormData(blank());
    setErrors({});
  };

  const handleEdit = (offer: Offer) => {
    setEditingId(offer.id);
    setFormData(offer);
    setErrors({});
    setIsAdding(true);
  };

  const handleDelete = async (id: string) => {
    await run(() => hzFetch(`/api/dashboard/offers/${id}`, { method: "DELETE" }), "Offer deleted");
    setConfirmDel(null);
  };

  const copyCode = (c: string) => {
    try { void navigator.clipboard?.writeText(c); } catch { /* ignore */ }
    setToast(`Code ${c} copied`);
  };
  const openNew = (preset?: Partial<Offer>) => {
    setEditingId(null);
    setFormData({ ...blank(), ...(preset ?? {}) });
    setErrors({});
    setIsAdding(true);
  };
  const duplicate = (o: Offer) => void run(() => hzFetch(`/api/dashboard/offers/${o.id}/duplicate`, { method: "POST" }), "Duplicate created and paused");
  const patch = (id: string, p: Partial<Offer>, msg: string) => {
    const action = "paused" in p ? (p.paused ? "pause" : "resume") : p.featured ? "feature" : "unfeature";
    void run(() => hzFetch(`/api/dashboard/offers/${id}/${action}`, { method: "POST" }), msg);
  };
  const toggleIn = (key: "inclusions" | "channels", v: string) =>
    setFormData((f) => ({ ...f, [key]: f[key].includes(v) ? f[key].filter((x) => x !== v) : [...f[key], v] }));

  const formatPrice = (amount: number) => {
    return currency === "NGN" ? `${currencySymbol}${Math.round(amount).toLocaleString()}` : `${currencySymbol}${Math.round(amount / USD_RATE).toLocaleString()}`;
  };

  const counts: Record<string, number> = { All: offers.length };
  offers.forEach((o) => { const s = statusOf(o); counts[s] = (counts[s] ?? 0) + 1; });
  const shown = offers.filter((o) => (filter === "All" || statusOf(o) === filter) && (!find.trim() || [o.title, o.roomCategory, o.code, o.type].some((x) => x.toLowerCase().includes(find.trim().toLowerCase()))));
  const expiring = offers.filter((o) => statusOf(o) === "Active" && o.validTo <= off(14)).length;
  const redemptions = offers.reduce((t, o) => t + o.used, 0);
  const err = (k: string) => (errors[k] ? { validationMessage: errors[k], validationState: "error" as const } : {});

    return (
    <>
      <div className={styles.modalOverlay}>
        <div className={styles.modalDialog}>
          <div className={`${styles.modalContent}`}>
            <header className={styles.modalHeader}>
              <h2>Manage Featured Offers</h2>
              <Button appearance="transparent" size="small" icon={<Dismiss24Regular />} onClick={onClose} aria-label="Close" />
            </header>

            <div className={`${styles.modalBody} ${styles.customScrollBar}`}>
              {/* Summary */}
              <div className={styles.roTiles}>
                <div className={styles.roTile}><span>Active offers</span><strong>{counts.Active ?? 0}</strong></div>
                <div className={styles.roTile}><span>Scheduled</span><strong>{counts.Scheduled ?? 0}</strong></div>
                <div className={styles.roTile}><span>Ending in 14 days</span><strong>{expiring}</strong></div>
                <div className={styles.roTile}><span>Redemptions</span><strong>{redemptions}</strong></div>
              </div>

              {/* Toolbar */}
              <div className={styles.toolbar}>
                <div className={styles.roBtns}>
                  <Button appearance="primary" size="small" icon={<Add16Regular />} onClick={() => openNew()}>
                    Add New Offer
                  </Button>
                  <Button size="small" icon={<Sparkle16Regular />} onClick={() => document.getElementById("ro-ai")?.scrollIntoView({ behavior: "smooth", block: "start" })}>
                    Suggest offers
                  </Button>
                </div>
                <Input className={styles.roSearch} size="small" contentBefore={<Search16Regular />} placeholder="Search title, room or code" value={find} onChange={(_, d) => setFind(d.value)} />
                <RadioGroup value={currency} onChange={(_, data) => setCurrency(data.value as "NGN" | "USD")} layout="horizontal">
                  <Radio value="NGN" label="NGN (₦)" />
                  <Radio value="USD" label="USD ($)" />
                </RadioGroup>
              </div>

              <TabList size="small" selectedValue={filter} onTabSelect={(_, d) => setFilter(String(d.value))} className={styles.roTabs}>
                {["All", "Active", "Scheduled", "Paused", "Expired", "Sold out"].map((s) => (
                  <Tab key={s} value={s}>{s} ({counts[s] ?? 0})</Tab>
                ))}
              </TabList>

              {/* Offers List */}
              {loadError && (
                <MessageBar intent="error" className={styles.roMsg}><MessageBarBody>{loadError}</MessageBarBody></MessageBar>
              )}
              {loading ? (
                <div className={styles.loading}>
                  <Spinner label="Loading offers..." />
                </div>
              ) : shown.length === 0 ? (
                <Text block align="center" className={styles.emptyState}>
                  {offers.length === 0 ? 'No featured offers yet. Click "Add New Offer" to create one.' : "No offers match this filter."}
                </Text>
              ) : (
                <div className={styles.offersGrid}>
                  {shown.map((offer) => {
                    const st = statusOf(offer);
                    const disc = discountOf(offer);
                    return (
                      <div key={offer.id} className={`${styles.offerCard} ${styles.roCard} ${st === "Expired" || st === "Sold out" ? styles.roDim : ""}`}>
                        <div className={styles.roBody}>
                          <div className={styles.roTop}>
                            <span className={`${styles.roType} ${styles["roType_" + offer.type.replace(/\s/g, "")] ?? ""}`}><i />{offer.type}</span>
                            <span className={styles.roTopRight}>
                              <span className={`${styles.roStatus} ${styles["roSt_" + st.replace(/\s/g, "")]}`}>{st}</span>
                              <button type="button" className={styles.roStar} aria-label={offer.featured ? "Remove from featured" : "Feature this offer"} onClick={() => patch(offer.id, { featured: !offer.featured }, offer.featured ? "Removed from featured" : "Marked as featured")}>
                                {offer.featured ? <Star16Filled /> : <Star16Regular />}
                              </button>
                            </span>
                          </div>
                          <div>
                            <h3 className={styles.roTitle}>{offer.title}</h3>
                            <p className={styles.roRoom}><Bed16Regular />{offer.roomCategory}</p>
                          </div>
                          {offer.description && <Text block className={styles.roDesc}>{offer.description}</Text>}

                          <div className={styles.roPrice}>
                            <div className={styles.roPriceMain}>
                              <strong>{formatPrice(offer.price)}</strong>
                              <span className={styles.roBasis}>{offer.priceBasis}</span>
                              {offer.originalPrice && <del className={styles.roWas}>{formatPrice(offer.originalPrice)}</del>}
                            </div>
                            {disc > 0 && <span className={styles.roSave}>{disc}% off</span>}
                          </div>

                          <div className={styles.roFacts}>
                            <span><WeatherMoon16Regular />{offer.minNights}+ night{offer.minNights === 1 ? "" : "s"}</span>
                            <span><People16Regular />Up to {offer.maxGuests}</span>
                            <span><Calendar16Regular />{fdate(offer.validFrom).replace(/ \d{4}$/, "")} – {fdate(offer.validTo)}</span>
                          </div>

                          {offer.inclusions.length > 0 && (
                            <ul className={styles.roIncl}>
                              {offer.inclusions.slice(0, 4).map((i) => <li key={i}><Checkmark12Regular />{i}</li>)}
                              {offer.inclusions.length > 4 && <li className={styles.roMore}>+{offer.inclusions.length - 4} more</li>}
                            </ul>
                          )}

                          <div className={styles.roUse}>
                            <div className={styles.roUseTop}><span>{offer.used} of {offer.limit} redeemed</span><span>{Math.round((offer.used / offer.limit) * 100)}%</span></div>
                            <span className={styles.roTrack}><span className={`${styles.roFill} ${offer.used >= offer.limit ? styles.roFillFull : ""}`} style={{ width: `${Math.min((offer.used / offer.limit) * 100, 100)}%` }} /></span>
                          </div>

                          {offer.code ? (
                            <button type="button" className={styles.roCodeBox} onClick={() => copyCode(offer.code)} aria-label={`Copy code ${offer.code}`}>
                              <span>Promo code</span><code>{offer.code}</code><em><Copy16Regular />Copy</em>
                            </button>
                          ) : (
                            <div className={`${styles.roCodeBox} ${styles.roCodeNone}`}><span>No promo code. Applied automatically.</span></div>
                          )}
                        </div>
                        <div className={styles.roFoot}>
                          <Button size="small" appearance="subtle" icon={<Edit16Regular />} onClick={() => handleEdit(offer)}>Edit</Button>
                          <Button size="small" appearance="subtle" icon={<Copy16Regular />} onClick={() => duplicate(offer)}>Duplicate</Button>
                          {st !== "Expired" && st !== "Sold out" && (
                            <Button size="small" appearance="subtle" icon={offer.paused ? <Play16Regular /> : <Pause16Regular />} onClick={() => patch(offer.id, { paused: !offer.paused }, offer.paused ? "Offer resumed" : "Offer paused")}>{offer.paused ? "Resume" : "Pause"}</Button>
                          )}
                          <span className={styles.roGrow} />
                          <Button size="small" appearance="subtle" icon={<Delete16Regular />} onClick={() => setConfirmDel(offer)} aria-label="Delete offer" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* AI suggestions */}
              <section id="ro-ai" className={styles.roAi}>
                <h3 className={styles.roAiHead}><Sparkle16Regular /> Offer ideas</h3>
                <p className={styles.roAiNote}>Suggested from common demand patterns. Review the details before you publish.</p>
                <div className={styles.roAiGrid}>
                  {AI_IDEAS.map((i) => (
                    <div key={i.title} className={styles.roIdea}>
                      <strong>{i.title}</strong>
                      <p>{i.why}</p>
                      <Button size="small" icon={<Add16Regular />} onClick={() => openNew(i.offer)}>Use this idea</Button>
                    </div>
                  ))}
                </div>
              </section>
            </div>
            {toast && <div className={styles.roToast} role="status" aria-live="polite">{toast}</div>}
          </div>
        </div>
      </div>

      {/* Add/Edit Offer Dialog */}
      <Dialog open={isAdding} onOpenChange={(_, { open }) => !open && (setIsAdding(false), setEditingId(null))}>
        <DialogSurface className={styles.roDialog}>
          <DialogBody>
            <DialogTitle
              action={
                <DialogTrigger disableButtonEnhancement>
                  <Button appearance="subtle" size="small" icon={<Dismiss24Regular />} aria-label="Close" />
                </DialogTrigger>
              }
            >
              {editingId ? "Edit Offer" : "Add New Featured Offer"}
            </DialogTitle>

            <DialogContent>
              {Object.keys(errors).length > 0 && (
                <MessageBar intent="error" className={styles.roMsg}><MessageBarBody>Fix the highlighted fields to continue.</MessageBarBody></MessageBar>
              )}
              <div className={styles.formGrid}>
                <Field label="Offer Title" required {...err("title")} className={styles.roFull}>
                  <Input
                    value={formData.title || ""}
                    onChange={(_, d) => setFormData({ ...formData, title: d.value })}
                  />
                </Field>

                <Field label="Offer type">
                  <Dropdown
                    value={formData.type}
                    selectedOptions={[formData.type]}
                    onOptionSelect={(_, d) => d.optionValue && setFormData({ ...formData, type: d.optionValue })}
                  >
                    {OFFER_TYPES.map((t) => <Option key={t} value={t}>{t}</Option>)}
                  </Dropdown>
                </Field>

                <Field label="Room Category" required {...err("roomCategory")}>
                  <Dropdown
                    value={formData.roomCategory}
                    selectedOptions={formData.roomCategory ? [formData.roomCategory] : []}
                    onOptionSelect={(_, d) =>
                      d.optionValue && setFormData({ ...formData, roomCategory: d.optionValue })
                    }
                    placeholder="Select a room category"
                  >
                    {roomCategories.map((cat) => (
                      <Option key={cat} value={cat}>
                        {cat}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>

                <Field label="Description" className={styles.roFull}>
                  <Textarea
                    resize="vertical"
                    value={formData.description || ""}
                    onChange={(_, d) => setFormData({ ...formData, description: d.value })}
                  />
                </Field>

                <Field label={`Offer Price (₦)`} required {...err("price")}>
                  <Input
                    type="number"
                    value={formData.price ? formData.price.toString() : ""}
                    onChange={(_, d) => setFormData({ ...formData, price: Number(d.value) || 0 })}
                  />
                </Field>

                <Field label="Original Price (optional)" {...err("originalPrice")}>
                  <Input
                    type="number"
                    value={formData.originalPrice?.toString() || ""}
                    onChange={(_, d) => setFormData({ ...formData, originalPrice: Number(d.value) || undefined })}
                  />
                </Field>

                <Field label="Price applies">
                  <Dropdown value={formData.priceBasis} selectedOptions={[formData.priceBasis]} onOptionSelect={(_, d) => d.optionValue && setFormData({ ...formData, priceBasis: d.optionValue as Offer["priceBasis"] })}>
                    <Option value="per night">per night</Option>
                    <Option value="per package">per package</Option>
                  </Dropdown>
                </Field>

                <Field label="Discount shown">
                  <div className={styles.roCalc}>{formData.originalPrice && formData.originalPrice > formData.price && formData.price > 0 ? `${Math.round((1 - formData.price / formData.originalPrice) * 100)}% off` : "No discount badge"}</div>
                </Field>

                <Field label="Valid From">
                  <Input
                    type="date"
                    value={formData.validFrom || ""}
                    onChange={(_, d) => setFormData({ ...formData, validFrom: d.value })}
                  />
                </Field>

                <Field label="Valid To" {...err("validTo")}>
                  <Input
                    type="date"
                    value={formData.validTo || ""}
                    onChange={(_, d) => setFormData({ ...formData, validTo: d.value })}
                  />
                </Field>

                <Field label="Minimum nights" {...err("minNights")}>
                  <Input type="number" value={String(formData.minNights)} onChange={(_, d) => setFormData({ ...formData, minNights: Number(d.value) || 0 })} />
                </Field>

                <Field label="Maximum guests" {...err("maxGuests")}>
                  <Input type="number" value={String(formData.maxGuests)} onChange={(_, d) => setFormData({ ...formData, maxGuests: Number(d.value) || 0 })} />
                </Field>

                <Field label="Promo code (optional)" {...err("code")}>
                  <Input value={formData.code} onChange={(_, d) => setFormData({ ...formData, code: d.value.toUpperCase().replace(/\s/g, "") })} />
                </Field>

                <Field label="Usage limit" {...err("limit")}>
                  <Input type="number" value={String(formData.limit)} onChange={(_, d) => setFormData({ ...formData, limit: Number(d.value) || 0 })} />
                </Field>

                <Field label="Cancellation">
                  <Dropdown value={formData.cancellation} selectedOptions={[formData.cancellation]} onOptionSelect={(_, d) => d.optionValue && setFormData({ ...formData, cancellation: d.optionValue })}>
                    {CANCELLATION.map((c) => <Option key={c} value={c}>{c}</Option>)}
                  </Dropdown>
                </Field>

                <Field label="Show on homepage">
                  <Switch checked={formData.featured} onChange={(_, d) => setFormData({ ...formData, featured: d.checked })} label={formData.featured ? "Featured" : "Not featured"} />
                </Field>

                <div className={styles.roFull}>
                  <Label>What's included</Label>
                  <div className={styles.roPick}>
                    {INCLUSION_PRESETS.map((i) => (
                      <button key={i} type="button" aria-pressed={formData.inclusions.includes(i)} className={`${styles.roPickBtn} ${formData.inclusions.includes(i) ? styles.roPickOn : ""}`} onClick={() => toggleIn("inclusions", i)}>{i}</button>
                    ))}
                  </div>
                </div>

                <div className={styles.roFull}>
                  <Label>Sell on</Label>
                  <div className={styles.roPick}>
                    {CHANNELS.map((c) => (
                      <button key={c} type="button" aria-pressed={formData.channels.includes(c)} className={`${styles.roPickBtn} ${formData.channels.includes(c) ? styles.roPickOn : ""}`} onClick={() => toggleIn("channels", c)}>{c}</button>
                    ))}
                  </div>
                  {errors.channels && <p className={styles.roErr}>{errors.channels}</p>}
                </div>
              </div>
            </DialogContent>

            <DialogActions>
              <Button appearance="secondary" size="small" onClick={() => setIsAdding(false)}>
                Cancel
              </Button>
              <Button appearance="primary" size="small" icon={<Save24Regular />} onClick={handleSaveOffer} disabled={saving}>
                {editingId ? "Save Changes" : "Create Offer"}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDel} onOpenChange={(_, d) => !d.open && setConfirmDel(null)}>
        <DialogSurface className={styles.roDialogSm}>
          <DialogBody>
            <DialogTitle>Delete this offer?</DialogTitle>
            <DialogContent>
              {confirmDel ? `${confirmDel.title} will be removed${confirmDel.used > 0 ? ` and its ${confirmDel.used} redemptions will no longer show here` : ""}. This can't be undone.` : ""}
            </DialogContent>
            <DialogActions>
              <Button size="small" onClick={() => setConfirmDel(null)}>Cancel</Button>
              <Button size="small" appearance="primary" className={styles.roDanger} disabled={saving} onClick={() => confirmDel && handleDelete(confirmDel.id)}>Delete</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};

export default RoomOffers;